/**
 * BadgeToast — small celebratory toast shown when the user earns a new
 * badge, with a Web Share API "share this badge" button.
 *
 * Consumers: App.tsx renders one of these per unread earned badge in the
 * user queue.
 */
import React, { useCallback } from "react";
import { Share2 } from "lucide-react";
import type { BadgeDef, Lang } from "../types/index.ts";
import { t } from "../i18n/index.ts";

export function BadgeToast({ badge, lang, onClose }: { badge: BadgeDef; lang: Lang; onClose: () => void }) {
  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          text: t("share.badgeText", lang).replace("{badge}", badge.name[lang] || badge.name.en || ""),
          url: "https://www.fornland.com",
        });
      } catch { /* user cancelled */ }
    }
  }, [badge, lang]);

  return (
    <div style={{
      position: "fixed", top: "calc(max(12px, env(safe-area-inset-top)) + 60px)", left: "50%", transform: "translateX(-50%)",
      zIndex: 100, padding: "14px 20px", borderRadius: "var(--radius-sm)",
      background: "var(--bg-glass)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
      border: "1px solid var(--border-strong)", boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      display: "flex", alignItems: "center", gap: 12, minWidth: 220,
      animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <span style={{ fontSize: 24 }}>{badge.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", letterSpacing: 0.2 }}>{badge.name[lang]}</div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{badge.description[lang]}</div>
      </div>
      {"share" in navigator && (
        <button onClick={handleShare} style={{
          width: 32, height: 32, border: "none", borderRadius: "var(--radius-lg)", background: "var(--bg-hover)",
          color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Share2 size={14} />
        </button>
      )}
      <button onClick={onClose} style={{
        width: 24, height: 24, border: "none", borderRadius: "var(--radius-lg)", background: "none",
        color: "var(--text-muted)", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>{"\u00d7"}</button>
    </div>
  );
}
